import type {
  GetListParams,
  GetListResult,
  GetManyParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  UpdateParams,
  UpdateResult,
  DeleteParams,
  DeleteResult,
} from "ra-core";
import { toRaAddress, type AddressRaRecord } from "./Mappers";
import type { PageQuery } from "@/domain/port/PageQuery";
import type { AddressService } from "@/domain/port/AddressService";
import { asAddressId } from "@/domain/model/Ids";
import type { UpdateAddressCommand } from "@/domain/model/Address";

export function makeAddressAdapter(addresses: AddressService) {
  return {
    async getList(
      _resource: string,
      params: GetListParams,
    ): Promise<GetListResult<AddressRaRecord>> {
      const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 };
      const { field, order } = params.sort ?? {
        field: "createdAt",
        order: "DESC",
      };
      const filter = params.filter ?? {};
      const q: PageQuery = {
        page,
        pageSize: perPage,
        sortBy: field,
        sortDir: order.toLowerCase() as "asc" | "desc",
        filter,
      };
      const res = await addresses.list(q);
      return { data: res.items.map(toRaAddress), total: res.total };
    },

    async getOne(
      _resource: string,
      params: GetOneParams,
    ): Promise<GetOneResult<AddressRaRecord>> {
      const item = await addresses.getById(asAddressId(params.id));
      return { data: toRaAddress(item) };
    },

    async update(
      _resource: string,
      params: UpdateParams,
    ): Promise<UpdateResult<AddressRaRecord>> {
      const updated = await addresses.update(
        asAddressId(params.id),
        params.data as UpdateAddressCommand,
      );
      return { data: toRaAddress(updated) };
    },

    async delete(
      _resource: string,
      params: DeleteParams,
    ): Promise<DeleteResult<AddressRaRecord>> {
      const deleted = await addresses.delete(asAddressId(params.id));
      return { data: toRaAddress(deleted) };
    },

    async getMany(
      _resource: string,
      params: GetManyParams,
    ): Promise<GetManyResult<AddressRaRecord>> {
      const ids = params.ids.map(asAddressId);
      const items = await addresses.getManyByIds(ids);
      return { data: items.map(toRaAddress) };
    },
  };
}
